import React from 'react';
import '../styles/privacy-policy.css';
import SEO from "../components/SEO";
import { PAGE_SEO } from "../seo-data";

export default function PrivacyPolicy() {
  return (
    <div className="pp-page" data-testid="privacy-policy-page">
      <SEO {...PAGE_SEO.privacyPolicy} />
      <div className="pp-container">
        <h1>Privacy Policy</h1>
        <p className="pp-updated">Last Updated: February 27, 2026</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Skye AI LLC ("Skye AI," "we," "us," or "our"), located in Fort Lauderdale, Florida, 
            is committed to protecting your privacy. This Privacy Policy explains how we collect, use, 
            disclose, and safeguard your information when you visit our website and use our services.
          </p>
          <p>
            By accessing or using our website, you agree to this Privacy Policy. If you do not agree 
            with the terms of this Privacy Policy, please do not access the site.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          <h3>Personal Information You Provide</h3>
          <p>We may collect personal information that you voluntarily provide when you:</p>
          <ul>
            <li>Use our AI Demo or AI Audit tools (name, email address, website URL)</li>
            <li>Interact with our AI chat widget (name, email address, business website)</li>
            <li>Contact us via email</li>
            <li>Use our "Get Started" flow</li>
          </ul>

          <h3>Automatically Collected Information</h3>
          <p>When you visit our website, we may automatically collect certain information, including:</p>
          <ul>
            <li>Browser type and version</li>
            <li>Operating system</li>
            <li>Device type (desktop, mobile, tablet)</li>
            <li>Pages visited and time spent on pages</li>
            <li>Referral source (how you found our website)</li>
            <li>Screen resolution</li>
          </ul>
          <p>
            This information is collected through our internal analytics system and is used solely 
            to improve our website and services. We do not use third-party analytics services for this purpose.
          </p>
        </section>

        <section>
          <h2>3. Third-Party Services</h2>
          <p>Our website uses the following third-party services:</p>
          <ul>
            <li>
              <strong>Meta (Facebook) Pixel:</strong> We use the Meta Pixel for advertising measurement 
              and optimization purposes. Meta may collect information about your browsing activity on our 
              site. For more information, please review{' '}
              <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer">
                Meta's Privacy Policy
              </a>.
            </li>
            <li>
              <strong>iframes.ai:</strong> We embed an interactive AI voice demo on our website. This 
              service may request microphone access if you choose to interact with it.
            </li>
            <li>
              <strong>testimonial.to:</strong> We embed video testimonials from our clients on our website.
            </li>
          </ul>
        </section>

        <section>
          <h2>4. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, and improve our website and services</li>
            <li>Respond to your inquiries and communicate with you</li>
            <li>Deliver the AI audit reports and demo experiences you request</li>
            <li>Analyze website usage to improve user experience</li>
            <li>Send you information about our services (only if you have provided your contact details)</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2>5. Data Storage and Security</h2>
          <p>
            Your personal information is stored securely using industry-standard encryption and 
            security measures. We retain your data only for as long as necessary to fulfill the 
            purposes outlined in this policy or as required by law.
          </p>
          <p>
            While we implement commercially reasonable security measures, no method of transmission 
            over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2>6. Sharing of Information</h2>
          <p>We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:</p>
          <ul>
            <li>With your consent</li>
            <li>To comply with legal obligations, court orders, or legal processes</li>
            <li>To protect the rights, property, or safety of Skye AI LLC, our users, or the public</li>
            <li>With service providers who assist us in operating our website (subject to confidentiality agreements)</li>
          </ul>
        </section>

        <section>
          <h2>7. Cookies</h2>
          <p>
            Our website uses minimal local storage (not cookies) to maintain session information 
            such as a random visitor identifier for analytics purposes. This identifier is anonymous 
            and cannot be used to personally identify you. You can clear this data at any time by 
            clearing your browser's local storage.
          </p>
        </section>

        <section>
          <h2>8. Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Request access to the personal information we hold about you</li>
            <li>Request correction of inaccurate personal information</li>
            <li>Request deletion of your personal information</li>
            <li>Opt out of any marketing communications</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at{' '}
            <a href="mailto:info@skyeai.io">info@skyeai.io</a>.
          </p>
        </section>

        <section>
          <h2>9. California Residents (CCPA)</h2>
          <p>
            If you are a California resident, you have additional rights under the California Consumer 
            Privacy Act (CCPA), including the right to know what personal information we collect, 
            the right to request deletion, and the right to opt out of the sale of personal information. 
            We do not sell personal information.
          </p>
        </section>

        <section>
          <h2>10. Children's Privacy</h2>
          <p>
            Our website and services are not intended for individuals under the age of 18. We do not 
            knowingly collect personal information from children. If you believe we have collected 
            information from a minor, please contact us immediately at{' '}
            <a href="mailto:info@skyeai.io">info@skyeai.io</a>.
          </p>
        </section>

        <section>
          <h2>11. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. Any changes will be posted on this 
            page with an updated "Last Updated" date. We encourage you to review this policy periodically.
          </p>
        </section>

        <section>
          <h2>12. Contact Us</h2>
          <p>If you have questions or concerns about this Privacy Policy, please contact us:</p>
          <div className="pp-contact">
            <p><strong>Skye AI LLC</strong></p>
            <p>Fort Lauderdale, FL</p>
            <p>Email: <a href="mailto:info@skyeai.io">info@skyeai.io</a></p>
          </div>
        </section>
      </div>
    </div>
  );
}
